"use client";

/**
 * Vue partagée de la home chauffeur (`/chauffeur` FR · `/en/chauffeur` EN).
 *
 * FR et EN rendent ce MÊME composant ; seul `locale` change. Tout le contenu +
 * chrome vient de CHAUFFEUR_HUB[locale] (hubDict). Les pages ne sont que des
 * wrappers. Les 6 sections (Hero, Services, Motion, Locations, SeoContent, Faq)
 * sont des composants locale-aware ; les 4 blocs restants (indicateurs,
 * process, atouts, services complémentaires) sont rendus ici.
 *
 * Les visuels sont identiques FR/EN → importés ici, pas dans le dictionnaire.
 */
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CitiesDirectory } from "@/components/seo/CitiesDirectory";
import { chauffeurCityLinks, eventLinks } from "@/utils/cityDirectory";
import HeroSection from "./HeroChaffeur";
import ServicesSection from "./ServicesSection";
import LocationsSection from "./LocationsSection";
import SeoContentSection from "./SeoContentSection";
import FaqSection from "./FaqSection";
import MotionExperienceSection from "./MotionExperienceSection";
import { CHAUFFEUR_HUB } from "./hubDict";
import QuickLeadForm from "@/components/forms/QuickLeadForm";
import { QUICK_LEAD_FORM_ID } from "@/components/forms/quickLeadFormUi";
import { TimeValueSimulator } from "@/components/conversion/TimeValueSimulator";
import { ChauffeurForfaitsTrust } from "./ChauffeurForfaitsTrust";
import { DisposalPricingSection } from "@/components/pricing/sections";
import { ReviewsSection } from "@/components/social-proof/ReviewsSection";
import imageService from "@/assets/images/chauffeur/services/Experience-passager.png";
import imageAeroport from "@/assets/images/chauffeur/aeroport/Transferts-aeroports-heliports.png";
import imageAtoutAeroport from "@/assets/images/chauffeur/transfert/atouts/Suivi-de-vol.png";
import imageAtoutEvents from "@/assets/images/chauffeur/events/avantages/Chef-de-projet.png";
import imageAtoutEntreprise from "@/assets/images/chauffeur/entreprise/atouts/SLA-fiabilite.png";
import imageAtoutCannes from "@/assets/images/chauffeur/cannes/atouts/Acces-evenements.png";
import imageAtoutCoteAzur from "@/assets/images/chauffeur/cote-azur/atouts/Connaissance-locale.png";
import imageAtoutServices from "@/assets/images/chauffeur/services/atouts/Ponctualite.png";

const ATOUT_IMAGES = [
  imageAtoutAeroport,
  imageAtoutEvents,
  imageAtoutEntreprise,
  imageAtoutCannes,
  imageAtoutCoteAzur,
  imageAtoutServices,
];

type ChauffeurHubViewProps = { locale?: Locale };

export default function ChauffeurHubView({
  locale = "fr",
}: ChauffeurHubViewProps): JSX.Element {
  const hub = tr(CHAUFFEUR_HUB, locale);
  // Service générique (région) pour le sujet équipe / la pré-saisie du lead.
  const regionLabel = locale === "en" ? "French Riviera" : "Côte d'Azur";
  return (
    <main className="bg-white text-black" lang={locale}>
      <HeroSection locale={locale} ctaPrimaryHref={`#${QUICK_LEAD_FORM_ID}`} />
      <ServicesSection locale={locale} />
      <ChauffeurForfaitsTrust
        locale={locale}
        ctaHref={`#${QUICK_LEAD_FORM_ID}`}
      />
      {/* Suite logique des forfaits transfert : la grille horaire. Les tarifs
          de mise à disposition existaient dans le moteur de devis
          (VEHICLES.disposalPackages) sans jamais être publiés — la prestation
          était citée partout, jamais chiffrée. */}
      <DisposalPricingSection locale={locale} />
      <ReviewsSection locale={locale} />

      <section className="border-y border-black/10 bg-gradient-to-br from-white via-neutral-100 to-white py-14">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
            className="mb-6 font-space-age text-lg tracking-[0.2em] text-black"
          >
            {hub.indicators.heading}
          </motion.h2>
          <div className="grid gap-4 md:grid-cols-4">
            {hub.indicators.items.map((item, index) => (
              <motion.article
                key={item.label}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ y: -2 }}
                className="rounded-2xl border border-black/15 bg-black/[0.02] p-5"
              >
                <p className="text-xs uppercase tracking-[0.18em] text-gold-800">
                  {item.label}
                </p>
                <p className="mt-2 text-base font-semibold text-black">
                  {item.value}
                </p>
                <p className="mt-2 text-sm text-black/70">{item.text}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <MotionExperienceSection locale={locale} />
      <LocationsSection locale={locale} />

      <section className="bg-white py-14 text-black">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.4 }}
            className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
          >
            {hub.fleet.eyebrow}
          </motion.p>
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
            className="mb-3 font-space-age text-xl tracking-[0.2em]"
          >
            {hub.fleet.heading}
          </motion.h2>
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="mb-6 max-w-3xl text-sm text-black/70"
          >
            {hub.fleet.intro}
          </motion.p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {hub.fleet.links.map((link, index) => (
              <motion.div
                key={link.href}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                whileHover={{ y: -2 }}
              >
                <Link
                  href={link.href}
                  className="block rounded-xl border border-black/10 bg-black/[0.02] px-4 py-3 text-sm font-medium text-black/80 transition hover:border-primary hover:text-black"
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 text-black">
        <div className="mx-auto grid max-w-[90rem] gap-8 px-6 md:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
          >
            <h2 className="mb-3 font-space-age text-xl tracking-[0.2em]">
              {hub.process.heading}
            </h2>
            <p className="text-black/72 text-sm">{hub.process.intro}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <motion.div
                whileHover={{ scale: 1.015 }}
                transition={{ duration: 0.25 }}
                className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-black/10"
              >
                <Image
                  src={imageService}
                  alt={hub.process.leftAlt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 30vw"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.015 }}
                transition={{ duration: 0.25 }}
                className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-black/10"
              >
                <Image
                  src={imageAeroport}
                  alt={hub.process.rightAlt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 30vw"
                />
              </motion.div>
            </div>
          </motion.div>
          <ol className="flex flex-col justify-end space-y-3">
            {hub.process.steps.map((step, index) => (
              <motion.li
                key={step}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                whileHover={{ x: 2 }}
                className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm"
              >
                <p className="text-xs uppercase tracking-[0.18em] text-black/50">
                  {hub.process.stepWord} {index + 1}
                </p>
                <p className="mt-2 text-sm text-black/80">{step}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <SeoContentSection locale={locale} />

      <section className="bg-white py-14 text-black">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.4 }}
            className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
          >
            {hub.atouts.eyebrow}
          </motion.p>
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
            className="mb-3 font-space-age text-xl tracking-[0.2em]"
          >
            {hub.atouts.heading}
          </motion.h2>
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45, delay: 0.05 }}
            className="mb-6 max-w-3xl text-sm text-black/70"
          >
            {hub.atouts.intro}
          </motion.p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {hub.atouts.items.map((item, index) => (
              <motion.article
                key={item.title}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                whileHover={{ y: -3 }}
                className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm"
              >
                <div className="relative aspect-[21/9] w-full">
                  <Image
                    src={ATOUT_IMAGES[index] || imageAtoutServices}
                    alt={item.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-black">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm text-black/70">{item.text}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <TimeValueSimulator locale={locale} ctaHref={`#${QUICK_LEAD_FORM_ID}`} />

      <FaqSection locale={locale} />

      <QuickLeadForm
        context="chauffeur"
        serviceLabel={regionLabel}
        locale={locale}
      />

      <section className="bg-white pb-16">
        <div className="mx-auto max-w-[90rem] px-6 md:px-8">
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.4 }}
            className="mb-3 font-space-age text-base tracking-[0.2em] text-black/90"
          >
            {hub.complementary.heading}
          </motion.h2>
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
            className="mb-4 max-w-3xl text-sm text-black/70"
          >
            {hub.complementary.intro}
          </motion.p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {hub.complementary.links.map((link, index) => (
              <motion.div
                key={link.href}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                whileHover={{ y: -2 }}
              >
                <Link
                  href={link.href}
                  className="block rounded-xl border border-black/10 bg-black/[0.02] px-4 py-3 text-sm text-black/80 transition hover:border-primary hover:text-black"
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <CitiesDirectory
        kind="chauffeur"
        cities={chauffeurCityLinks(locale)}
        locale={locale}
      />
      <CitiesDirectory
        kind="events"
        cities={eventLinks(locale)}
        locale={locale}
      />
    </main>
  );
}
